import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { ChannelSummary, ChannelVisibility } from '@contracts/channels';
import { useChannelActions, useChannels } from '../api/queries';
import {
  Broken,
  Button,
  Card,
  Empty,
  Field,
  ListSwitch,
  Modal,
  Pill,
  Rows,
  SkeletonRows,
  TextArea,
  useNotices,
} from '../ui';
import { ago } from '../lib/time';

/**
 * Channels (PRD 58) — Fellow's shape: a group of people, and the meetings
 * posted to them. Replaces spaces and folders (Vladimir, 2026-09-24: "spaces
 * and folders now is useless and dont work").
 *
 * Yours first, then the public ones in the workspace you could join. A private
 * channel you are not in is not here at all — not even its name.
 */
export function Channels(): ReactElement {
  const channels = useChannels();
  const [params] = useSearchParams();
  const [creating, setCreating] = useState(params.get('new') === '1');
  const mine = channels.data?.mine ?? [];
  const others = channels.data?.others ?? [];
  return (
    // ⚠ The same width as Meetings and Series: the three are one switch, and a
    // narrower page made the switch jump sideways (Vladimir, 2026-09-24).
    <div className="page stack-lg">
      <ListSwitch on="channels" />
      <div className="page-head">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="t-title">Collections</h1>
          <p className="mono">
            a group of people, and the meetings in it — adding a meeting shares it with all of them
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>
          New collection
        </Button>
      </div>
      {channels.isPending ? (
        <SkeletonRows rows={4} />
      ) : channels.isError ? (
        <Card flush>
          <Broken what="Collections" retry={() => void channels.refetch()} />
        </Card>
      ) : (
        <>
          <Card title="Your collections" flush>
            {mine.length === 0 ? (
              <Empty lead="You are in no collection yet.">
                Make one for a team, a customer or a recurring meeting — then add meetings to it, or
                have a series add itself.
              </Empty>
            ) : (
              <Rows>
                {mine.map((c) => (
                  <ChannelRow key={c.id} channel={c} />
                ))}
              </Rows>
            )}
          </Card>
          {others.length > 0 ? (
            <Card
              title="More in your workspace"
              aside={<span className="mono">public — anybody may join</span>}
              flush
            >
              <Rows>
                {others.map((c) => (
                  <ChannelRow key={c.id} channel={c} />
                ))}
              </Rows>
            </Card>
          ) : null}
        </>
      )}
      {creating ? <NewChannel onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function ChannelRow({ channel: c }: { channel: ChannelSummary }): ReactElement {
  return (
    <Link to={`/ch/${c.id}`} className="row">
      <span className="row-main grow">
        <span className="row-title">
          {c.name} {c.visibility === 'PRIVATE' ? <Pill>private</Pill> : null}
          {c.myRole === 'OWNER' ? <Pill tone="green">owner</Pill> : null}
        </span>
        {c.description ? <span className="row-sub">{c.description}</span> : null}
        <span className="row-sub mono">
          {c.memberCount} {c.memberCount === 1 ? 'person' : 'people'} · {c.meetingCount}{' '}
          {c.meetingCount === 1 ? 'meeting' : 'meetings'}
          {c.lastPostedAt ? ` · last added ${ago(c.lastPostedAt)}` : ''}
        </span>
      </span>
    </Link>
  );
}

/** Name it, say what it is for, and who can find it. */
export function NewChannel({ onClose }: { onClose: () => void }): ReactElement {
  const actions = useChannelActions();
  const navigate = useNavigate();
  const { show } = useNotices();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<ChannelVisibility>('PUBLIC');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    actions.create.mutate(
      { name: name.trim(), description: description.trim() || null, visibility },
      {
        onSuccess: (made) => {
          onClose();
          void navigate(`/ch/${made.id}`);
          show({ tone: 'success', text: `${made.name} is ready — add a meeting to it.` });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };
  return (
    <Modal
      title="New collection"
      onClose={onClose}
      foot={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            form="new-channel"
            disabled={!name.trim()}
            working={actions.create.isPending ? 'Making…' : false}
          >
            Make it
          </Button>
        </>
      }
    >
      <form id="new-channel" className="stack" style={{ gap: 12 }} onSubmit={submit}>
        <Field
          label="Name"
          autoFocus
          placeholder="dev-team-leads, Forestar calls…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
        />
        <TextArea
          label="What it is for"
          placeholder="Optional"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={300}
        />
        <div className="stack" style={{ gap: 6 }}>
          <span className="t-secondary">Who can find it</span>
          <div className="cluster" style={{ gap: 6 }}>
            <Button
              size="sm"
              variant={visibility === 'PUBLIC' ? 'primary' : 'quiet'}
              onClick={() => setVisibility('PUBLIC')}
            >
              Anybody in the workspace
            </Button>
            <Button
              size="sm"
              variant={visibility === 'PRIVATE' ? 'primary' : 'quiet'}
              onClick={() => setVisibility('PRIVATE')}
            >
              Only people in it
            </Button>
          </div>
          <span className="t-secondary">
            {visibility === 'PUBLIC'
              ? 'Anybody in the workspace can find it and join; joining shows them what is posted.'
              : 'Nobody else sees it exists. The people in it add others.'}
          </span>
        </div>
      </form>
    </Modal>
  );
}
